import {
  UseMutationOptions,
  UseQueryOptions,
  mutationOptions,
  queryOptions,
} from '@tanstack/react-query';
import {
  UserStatus as AccountStatus,
  EntityType,
} from 'adc-sirius-module-common';
import mediator from '../utilities/mediator';
import log from '../utilities/log';
import {
  Account,
  AccountCreationRequest,
  AccountCreationResult,
  AccountRoleAssignmentsUpdateRequest,
  AccountUpdateRequest,
  AccountWithRoleAssignments,
  ApiErrorResponse,
} from '../models';

type AccountsQueryParams = {
  status?: AccountStatus;
};

type AccountQueryParams = {
  key?: string;
};

type CreateAccountVariables = {
  account: AccountCreationRequest;
};

type UpdateAccountVariables = {
  account: AccountUpdateRequest;
};

export type UpdateAccountRolesVariables = {
  updateRequest: AccountRoleAssignmentsUpdateRequest;
};

export function accountsQueryOptions(
  params: AccountsQueryParams = {},
  options?: Partial<UseQueryOptions<Account[]>>,
) {
  return queryOptions<Account[]>({
    ...options,
    queryKey: ['accounts'],
    queryFn: async () => {
      try {
        return await mediator.request<Account[]>('account:getAccounts', {
          ...params,
        });
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message);
      }
    },
  });
}

export function accountQueryOptions(
  params: AccountQueryParams = {},
  options?: Partial<UseQueryOptions<Account>>,
) {
  return queryOptions<Account>({
    ...options,
    queryKey: ['account', params.key],
    queryFn: async () => {
      try {
        return await mediator.request<Account>('account:getAccountByKey', {
          ...params,
        });
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message);
      }
    },
    enabled: !!params.key,
  });
}

export function accountsByEntityQueryKey(clinEntityKey: string) {
  return ['accounts', 'clinentity', clinEntityKey] as const;
}

export function accountsByEntityQueryOptions(
  clinEntityKey: string,
  entityType: EntityType,
  options?: Partial<UseQueryOptions<AccountWithRoleAssignments[]>>,
) {
  return queryOptions<AccountWithRoleAssignments[]>({
    ...options,
    queryKey: accountsByEntityQueryKey(clinEntityKey),
    queryFn: async () => {
      try {
        return await mediator.request<AccountWithRoleAssignments[]>(
          'account:getAccountsByEntity',
          {
            key: clinEntityKey,
            entityType,
          },
        );
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message);
      }
    },
    enabled: !!clinEntityKey && options?.enabled !== false,
  });
}

export function createAccountMutateOptions(
  options?: Partial<
    UseMutationOptions<AccountCreationResult, Error, CreateAccountVariables>
  >,
) {
  return mutationOptions<AccountCreationResult, Error, CreateAccountVariables>({
    ...options,
    mutationFn: async ({ account }: CreateAccountVariables) => {
      try {
        return await mediator.request<AccountCreationResult>(
          'account:createAccount',
          {
            data: account,
          },
        );
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message, error.data);
      }
    },
  });
}

export function updateAccountMutateOptions(
  options?: Partial<UseMutationOptions<Account, Error, UpdateAccountVariables>>,
) {
  return mutationOptions<Account, Error, UpdateAccountVariables>({
    ...options,
    mutationFn: async ({ account }: UpdateAccountVariables) => {
      try {
        return await mediator.request<Account>('account:updateAccount', {
          data: account,
        });
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message, error.data);
      }
    },
  });
}

export function updateAccountRolesMutateOptions(
  options?: Partial<
    UseMutationOptions<
      AccountWithRoleAssignments[],
      Error,
      UpdateAccountRolesVariables
    >
  >,
) {
  return mutationOptions<
    AccountWithRoleAssignments[],
    Error,
    UpdateAccountRolesVariables
  >({
    ...options,
    mutationFn: async ({ updateRequest }: UpdateAccountRolesVariables) => {
      try {
        return await mediator.request<AccountWithRoleAssignments[]>(
          'account:updateAccountRoles',
          {
            data: updateRequest,
          },
        );
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message);
      }
    },
  });
}
